/**
 * ProjectsPage Component
 * 
 * Main page for managing user projects.
 * Displays all projects and provides ability to add, edit, and delete projects.
 * 
 * Features:
 * - Display list of all user projects
 * - Add new project with title, overview, and images
 * - Edit existing projects
 * - Delete projects with confirmation
 * - Real-time sync with Firebase
 * - Protected route (requires authentication)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../Auth/context/AuthContext';
import { loadUserProjects, deleteProject, updateProject } from '../Projects/utilities/projectsUtils';
import { loadUserSkills, updateSkill } from '../Skills/utilities/skillsUtils';
import { loadUserJobs } from '../../Jobs/utilities/jobsUtils';
import { recommendProjectsAndSkills } from '../../AI/utilities/aiLogicService';
import {
    applyPortfolioRecordUpdates,
    generatePortfolioRecordUpdates,
} from '../../AI/NewBlogSystem/utilities/portfolioRecordAiUtils.js';
import ProjectsSkillsAiAssistant from '../../AI/ProjectsAndSkills/ProjectsSkillsAiAssistant.jsx';
import { AiAssistantButton } from '../../AI/components/index.js';
import {
    loadAIRecommendations,
    removeAIRecommendation,
    saveAIRecommendations,
} from '../Projects/utilities/recommendationsUtils';
import PageContainer from '../../PageUtil/PageContainer';
import AddProjectForm from '../Projects/AddProjectForm';
import ProjectCard from '../Projects/ProjectCard';
import FeedbackNotice from '../../Common/Feedback/FeedbackNotice.jsx';
import SkillsMenu from '../Skills/SkillsMenu';
import ProjectsSkillsSummary, {
    CollapsibleRecommendationList,
} from '../Summary/ProjectsSkillsSummary';
import FixedMenu from '../../Common/Menus/FixedMenu';
import DesiredJobsManager from '../../Jobs/DesiredJobsManager';
import FirstPortfolioGuide from '../../Common/Onboarding/FirstPortfolioGuide';
import { loadUserPortfolios } from '../../BlogSystem/utilities/Portfolios/draftPortfolioUtils';
import '../assets/projectsAndSkills.scss';

const CONTENT_TABS = Object.freeze({
    PROJECTS: 'projects',
    SKILLS: 'skills',
    SUMMARY: 'summary',
    JOBS: 'jobs',
});

const isContentTab = (value) => Object.values(CONTENT_TABS).includes(value);

const ProjectsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [userId, setUserId] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const requestedTab = searchParams.get('tab');
    const activeContentTab = isContentTab(requestedTab) ? requestedTab : CONTENT_TABS.SUMMARY;
    const [showAiAssistant, setShowAiAssistant] = useState(false);
    const [aiInitialTask, setAiInitialTask] = useState('recommend');
    const [aiContextualTask, setAiContextualTask] = useState(null);
    const [returnToProjectForm, setReturnToProjectForm] = useState(false);
    const [projectFormDraft, setProjectFormDraft] = useState(null);
    const [editingProject, setEditingProject] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [recommendationLoading, setRecommendationLoading] = useState(false);
    const [recommendations, setRecommendations] = useState(null);
    const [skillsRevision, setSkillsRevision] = useState(0);
    const [skills, setSkills] = useState([]);
    const [skillsLoading, setSkillsLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [portfolioCount, setPortfolioCount] = useState(0);
    const [recommendedSkillDraft, setRecommendedSkillDraft] = useState(null);
    const [pendingRecommendation, setPendingRecommendation] = useState(null);
    const [aiQueueStatus, setAiQueueStatus] = useState({
        count: 0,
        working: false,
        needsAttention: false,
    });

    const setActiveContentTab = useCallback((tab) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (tab === CONTENT_TABS.SUMMARY) next.delete('tab');
            else next.set('tab', tab);
            return next;
        });
    }, [setSearchParams]);

    // Get current user ID
    useEffect(() => {
        if (user) {
            setUserId(user.uid);
        }
    }, [user]);

    // Load projects when userId changes
    useEffect(() => {
        if (userId) {
            loadProjects();
            void loadSavedRecommendations();
        }
    }, [userId]);

    useEffect(() => {
        let active = true;
        if (!userId) {
            setSkills([]);
            setJobs([]);
            return undefined;
        }

        setSkillsLoading(true);
        Promise.all([loadUserSkills(userId), loadUserJobs(userId)])
            .then(([loadedSkills, loadedJobs]) => {
                if (active) {
                    setSkills(Array.isArray(loadedSkills) ? loadedSkills : []);
                    setJobs(Array.isArray(loadedJobs) ? loadedJobs : []);
                }
            })
            .catch((loadError) => {
                console.error('Error loading portfolio context summary:', loadError);
                if (active) {
                    setSkills([]);
                    setJobs([]);
                }
            })
            .finally(() => {
                if (active) setSkillsLoading(false);
            });

        return () => {
            active = false;
        };
    }, [skillsRevision, userId]);

    useEffect(() => {
        let active = true;
        if (!userId) {
            setPortfolioCount(0);
            return undefined;
        }
        loadUserPortfolios(userId)
            .then((portfolios) => {
                if (active) setPortfolioCount(Array.isArray(portfolios) ? portfolios.length : 0);
            })
            .catch((loadError) => {
                console.error('Error loading onboarding portfolio count:', loadError);
                if (active) setPortfolioCount(0);
            });
        return () => { active = false; };
    }, [userId]);

    const handleCreatePortfolio = useCallback(() => {
        const title = window.prompt('Enter portfolio title:');
        if (title?.trim() && userId) {
            navigate(`/${userId}/drafts/editor/${encodeURIComponent(title.trim())}?new=true`);
        }
    }, [navigate, userId]);

    const loadSavedRecommendations = async () => {
        if (!userId) {
            return;
        }

        try {
            const saved = await loadAIRecommendations(userId);
            const hasProjects = Array.isArray(saved.projects) && saved.projects.length > 0;
            const hasSkills = Array.isArray(saved.skills) && saved.skills.length > 0;

            if (hasProjects || hasSkills) {
                setRecommendations({
                    projects: saved.projects,
                    skills: saved.skills,
                    updatedAt: saved.updatedAt || null,
                });
            }
        } catch (err) {
            console.error('Error loading saved AI recommendations:', err);
        }
    };

    /**
     * Load all projects for the current user
     */
    const loadProjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const userProjects = await loadUserProjects(userId);
            setProjects(userProjects || []);
        } catch (err) {
            console.error('Error loading projects:', err);
            setError('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle project added or updated
     */
    const handleProjectSaved = async (message) => {
        const addedRecommendation = pendingRecommendation?.type === 'project'
            ? pendingRecommendation
            : null;
        setSuccessMessage(message);
        setShowAddForm(false);
        setEditingProject(null);
        setProjectFormDraft(null);
        setPendingRecommendation(null);
        await loadProjects();
        if (addedRecommendation) {
            await removeSavedRecommendation(addedRecommendation);
        }
    };

    /**
     * Handle project deletion
     */
    const handleDeleteProject = async (projectId, projectTitle) => {
        if (window.confirm(`Are you sure you want to delete the project "${projectTitle}"?`)) {
            try {
                await deleteProject(userId, projectId);
                setSuccessMessage('Project deleted successfully');
                await loadProjects();
            } catch (err) {
                console.error('Error deleting project:', err);
                setError('Failed to delete project');
            }
        }
    };

    /**
     * Handle edit project
     */
    const handleEditProject = (project) => {
        setProjectFormDraft(null);
        setEditingProject(project);
        setShowAddForm(true);
    };

    /**
     * Handle cancel form
     */
    const handleCancelForm = () => {
        setShowAddForm(false);
        setEditingProject(null);
        setProjectFormDraft(null);
        if (pendingRecommendation?.type === 'project') setPendingRecommendation(null);
    };

    const captureProjectFormDraft = useCallback((draft) => {
        setProjectFormDraft(draft);
    }, []);

    const removeSavedRecommendation = async (recommendation) => {
        try {
            const next = await removeAIRecommendation(userId, recommendation);
            setRecommendations({ ...next, updatedAt: new Date() });
            return true;
        } catch (removeError) {
            console.error('Error removing completed recommendation:', removeError);
            setError('Failed to discard recommendation');
            return false;
        }
    };

    const discardRecommendation = async (type, recommendation) => {
        const removed = await removeSavedRecommendation({
            type,
            title: recommendation?.title,
            name: recommendation?.name,
        });
        if (removed) setSuccessMessage('Recommendation discarded');
    };

    const addRecommendedProject = useCallback((recommendation) => {
        setShowAiAssistant(false);
        setReturnToProjectForm(false);
        setAiContextualTask(null);
        setActiveContentTab(CONTENT_TABS.PROJECTS);
        setEditingProject(null);
        setProjectFormDraft({
            title: recommendation?.title || '',
            overview: recommendation?.overview || '',
            projectUrl: '',
            images: [],
            skillsUsed: [],
        });
        setPendingRecommendation({
            type: 'project',
            title: recommendation?.title || '',
        });
        setShowAddForm(true);
    }, [setActiveContentTab]);

    const addRecommendedSkill = useCallback((recommendation) => {
        setShowAiAssistant(false);
        setReturnToProjectForm(false);
        setAiContextualTask(null);
        setRecommendedSkillDraft({
            name: recommendation?.name || '',
            description: recommendation?.description || '',
            rating: 3,
            icon: null,
        });
        setPendingRecommendation({
            type: 'skill',
            name: recommendation?.name || '',
        });
        setActiveContentTab(CONTENT_TABS.SKILLS);
    }, [setActiveContentTab]);

    const setAiAssistantExpanded = (expanded) => {
        setShowAiAssistant(expanded);
        if (!expanded && returnToProjectForm) {
            setReturnToProjectForm(false);
            setShowAddForm(true);
        }
    };

    const handleGenerateRecommendations = async (request = '') => {
        if (!userId) {
            return;
        }

        setRecommendationLoading(true);
        try {
            const [skills, jobs] = await Promise.all([
                loadUserSkills(userId),
                loadUserJobs(userId),
            ]);

            if (!Array.isArray(jobs) || jobs.length === 0) {
                setRecommendations({ projects: [], skills: [] });
                throw new Error('Add at least one desired job before asking for recommendations.');
            }

            const result = await recommendProjectsAndSkills({
                projects,
                skills,
                jobs,
                request,
            });

            const saved = await saveAIRecommendations(userId, result, { append: false });
            setRecommendations({
                ...saved,
                updatedAt: new Date(),
            });
            return saved;
        } catch (err) {
            console.error('Error generating recommendations:', err);
            throw err;
        } finally {
            setRecommendationLoading(false);
        }
    };

    const handleGenerateRecordUpdates = async (request, scope = 'all') => {
        const skills = await loadUserSkills(userId);
        const updates = await generatePortfolioRecordUpdates({
            prompt: request,
            projects,
            skills,
            scope,
        });
        return { updates, context: { projects, skills }, scope };
    };

    const handleApplyRecordUpdates = async ({ updates, context, scope = 'all' }) => {
        await applyPortfolioRecordUpdates({
            userId,
            updates,
            projects: context?.projects || projects,
            skills: context?.skills || [],
            updateProject,
            updateSkill,
        });
        await loadProjects();
        setSkillsRevision((revision) => revision + 1);
        setSuccessMessage(scope === 'projects'
            ? 'AI project changes saved successfully'
            : scope === 'skills' ? 'AI skill changes saved successfully' : 'AI project and skill changes saved successfully');
    };

    return (
        <>
            <ProjectsSkillsAiAssistant
                expanded={showAiAssistant}
                setExpanded={setAiAssistantExpanded}
                onQueueStatusChange={setAiQueueStatus}
                userId={userId}
                initialTask={aiInitialTask}
                preferredTask={activeContentTab === CONTENT_TABS.SUMMARY || activeContentTab === CONTENT_TABS.JOBS
                    ? 'recommend'
                    : activeContentTab === CONTENT_TABS.SKILLS ? 'edit-skills' : 'edit-projects'}
                contextualTask={aiContextualTask}
                onGenerateRecommendations={handleGenerateRecommendations}
                onGenerateUpdates={handleGenerateRecordUpdates}
                onApplyUpdates={handleApplyRecordUpdates}
                onAddRecommendedProject={addRecommendedProject}
                onAddRecommendedSkill={addRecommendedSkill}
            />

            <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
                <div className="container py-5 projects-skills-page">
                    {/* Header Section */}
                    <div className="mb-5 px-3 px-md-0">
                        <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
                            <div>
                                <h1 className="h2 mb-1 fw-bold">
                                    <i className="bi bi-kanban me-2 color-primary"></i>
                                    Portfolio Context
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage the projects, skills, and desired jobs the AI uses to tailor your portfolio
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="nav nav-tabs nav-fill mb-4 px-3 px-md-0" role="tablist" aria-label="Portfolio content">
                        <button
                            type="button"
                            className={`nav-link py-3 ${activeContentTab === CONTENT_TABS.SUMMARY ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeContentTab === CONTENT_TABS.SUMMARY}
                            onClick={() => setActiveContentTab(CONTENT_TABS.SUMMARY)}
                        >
                            <i className="bi bi-grid-1x2 me-2" aria-hidden="true"></i>
                            Summary
                        </button>
                        <button
                            type="button"
                            className={`nav-link py-3 ${activeContentTab === CONTENT_TABS.JOBS ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeContentTab === CONTENT_TABS.JOBS}
                            onClick={() => setActiveContentTab(CONTENT_TABS.JOBS)}
                        >
                            <i className="bi bi-briefcase me-2" aria-hidden="true"></i>
                            Desired Jobs
                        </button>
                        <button
                            type="button"
                            className={`nav-link py-3 ${activeContentTab === CONTENT_TABS.SKILLS ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeContentTab === CONTENT_TABS.SKILLS}
                            onClick={() => setActiveContentTab(CONTENT_TABS.SKILLS)}
                        >
                            <i className="bi bi-award me-2" aria-hidden="true"></i>
                            Skills
                        </button>
                        <button
                            type="button"
                            className={`nav-link py-3 ${activeContentTab === CONTENT_TABS.PROJECTS ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeContentTab === CONTENT_TABS.PROJECTS}
                            onClick={() => setActiveContentTab(CONTENT_TABS.PROJECTS)}
                        >
                            <i className="bi bi-kanban me-2" aria-hidden="true"></i>
                            Projects
                        </button>
                    </div>
                    <FirstPortfolioGuide
                        jobCount={jobs.length}
                        skillCount={skills.length}
                        projectCount={projects.length}
                        portfolioCount={portfolioCount}
                        onOpenJobs={() => setActiveContentTab(CONTENT_TABS.JOBS)}
                        onOpenSkills={() => setActiveContentTab(CONTENT_TABS.SKILLS)}
                        onOpenProjects={() => setActiveContentTab(CONTENT_TABS.PROJECTS)}
                        onCreatePortfolio={handleCreatePortfolio}
                    />
                    <FeedbackNotice
                        message={successMessage || ''}
                        variant="success"
                        onDismiss={() => setSuccessMessage(null)}
                    />

                    {/* Project form */}
                    {activeContentTab === CONTENT_TABS.PROJECTS && showAddForm && (
                        <FixedMenu
                            title={editingProject ? 'Edit Project' : 'Create New Project'}
                            iconClass={editingProject ? 'bi-pencil-square' : 'bi-plus-lg'}
                            expanded={showAddForm}
                            setExpanded={handleCancelForm}
                        >
                            <AddProjectForm
                                userId={userId}
                                editingProject={editingProject}
                                restoredDraft={projectFormDraft}
                                onDraftChange={captureProjectFormDraft}
                                onProjectSaved={handleProjectSaved}
                                onCancel={handleCancelForm}
                                onOpenAI={(contextualTask) => {
                                    setAiContextualTask(contextualTask);
                                    setAiInitialTask('rewrite');
                                    setReturnToProjectForm(true);
                                    setShowAddForm(false);
                                    setShowAiAssistant(true);
                                }}
                            />
                        </FixedMenu>
                    )}

                    {activeContentTab === CONTENT_TABS.SUMMARY ? (
                        <ProjectsSkillsSummary
                            projectCount={projects.length}
                            skillCount={skills.length}
                            jobCount={jobs.length}
                            loading={loading || skillsLoading}
                            recommendations={recommendations}
                            onAddProject={addRecommendedProject}
                            onAddSkill={addRecommendedSkill}
                            onDiscardProject={(recommendation) => discardRecommendation('project', recommendation)}
                            onDiscardSkill={(recommendation) => discardRecommendation('skill', recommendation)}
                        />
                    ) : activeContentTab === CONTENT_TABS.SKILLS ? (
                        <div className="px-3 px-md-0" role="tabpanel" aria-label="Skills">
                            {Array.isArray(recommendations?.skills) && recommendations.skills.length ? (
                                <div className="projects-skills-tab-recommendations mb-4">
                                    <CollapsibleRecommendationList
                                        title="Recommended skills"
                                        iconClass="bi-lightbulb"
                                        items={recommendations.skills}
                                        type="skill"
                                        onAdd={addRecommendedSkill}
                                        onDiscard={(recommendation) => discardRecommendation('skill', recommendation)}
                                    />
                                </div>
                            ) : null}
                            <SkillsMenu
                                refreshKey={skillsRevision}
                                assistantOpen={showAiAssistant}
                                onSkillsLoaded={setSkills}
                                initialDraft={recommendedSkillDraft}
                                onInitialDraftConsumed={() => setRecommendedSkillDraft(null)}
                                onSkillSaved={() => {
                                    if (pendingRecommendation?.type === 'skill') {
                                        const completed = pendingRecommendation;
                                        setPendingRecommendation(null);
                                        void removeSavedRecommendation(completed);
                                    }
                                }}
                                onFormCancelled={() => {
                                    if (pendingRecommendation?.type === 'skill') {
                                        setPendingRecommendation(null);
                                    }
                                }}
                                onOpenAI={(contextualTask) => {
                                    setReturnToProjectForm(false);
                                    setAiContextualTask(contextualTask || null);
                                    setAiInitialTask(contextualTask ? 'rewrite' : 'edit-skills');
                                    setShowAiAssistant(true);
                                }}
                            />
                        </div>
                    ) : activeContentTab === CONTENT_TABS.JOBS ? (
                        <DesiredJobsManager userId={userId} onJobsLoaded={setJobs} />
                    ) : (
                        <div role="tabpanel" aria-label="Projects">
                            {Array.isArray(recommendations?.projects) && recommendations.projects.length ? (
                                <div className="px-3 px-md-0">
                                    <div className="projects-skills-tab-recommendations mb-4">
                                        <CollapsibleRecommendationList
                                            title="Recommended projects"
                                            iconClass="bi-lightbulb"
                                            items={recommendations.projects}
                                            type="project"
                                            onAdd={addRecommendedProject}
                                            onDiscard={(recommendation) => discardRecommendation('project', recommendation)}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mb-4 px-3 px-md-0">
                                <div>
                                    <h2 className="h4 mb-1">Your projects</h2>
                                    <p className="text-muted mb-0">
                                        {projects.length} project{projects.length === 1 ? '' : 's'} ready to use in your portfolio
                                    </p>
                                </div>
                                <button
                                    className="btn btn-success"
                                    onClick={() => {
                                        if (!showAddForm) setProjectFormDraft(null);
                                        setEditingProject(null);
                                        setShowAddForm(!showAddForm);
                                    }}
                                    title={showAddForm ? 'Cancel' : 'Add Project'}
                                >
                                    <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-2`} aria-hidden="true"></i>
                                    {showAddForm ? 'Cancel' : 'Add Project'}
                                </button>
                            </div>
                            {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                            ) : projects.length === 0 ? (
                        <div className="px-3 px-md-0">
                            <div className="alert alert-info rounded-3 p-4 text-center">
                                <i className="bi bi-info-circle me-2 fs-5"></i>
                                <div>
                                    <h5 className="alert-heading mb-2">No projects yet</h5>
                                    <p className="mb-3">Add a project that demonstrates your skills with real evidence the AI can showcase.</p>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setEditingProject(null);
                                            setShowAddForm(true);
                                        }}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Project
                                    </button>
                                </div>
                            </div>
                        </div>
                            ) : (
                        <div className="px-3 px-md-0">
                            <div className="row g-4 projects-management-grid">
                                {projects.map((project) => (
                                    <div key={project.id} className="col-xl-4 col-md-6">
                                        <ProjectCard
                                            project={project}
                                            userId={userId}
                                            onEdit={() => handleEditProject(project)}
                                            onDelete={() => handleDeleteProject(project.id, project.title)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                            )}
                        </div>
                    )}
                </div>
            </PageContainer>

            <AiAssistantButton
                className="projects-skills-floating-ai"
                onClick={() => {
                    setReturnToProjectForm(false);
                    setAiContextualTask(null);
                    setAiInitialTask(activeContentTab === CONTENT_TABS.SUMMARY || activeContentTab === CONTENT_TABS.JOBS
                        ? 'recommend'
                        : activeContentTab === CONTENT_TABS.SKILLS ? 'edit-skills' : 'edit-projects');
                    setShowAiAssistant(true);
                }}
                disabled={!userId || showAiAssistant}
                busy={recommendationLoading}
                queueCount={aiQueueStatus.count}
                needsAttention={aiQueueStatus.needsAttention}
                working={aiQueueStatus.working}
                label={activeContentTab === CONTENT_TABS.SUMMARY || activeContentTab === CONTENT_TABS.JOBS
                    ? 'Get AI recommendations'
                    : activeContentTab === CONTENT_TABS.SKILLS ? 'Edit skills with AI' : 'Edit projects with AI'}
            />
        </>
    );
};

export default ProjectsPage;
